/**
 * Prediction panel — geopolitical forecast and prediction markets.
 *
 * Filters events where source === 'prediction' or tags include
 * 'forecast'/'prediction'. Displays probability bars grouped by category.
 * Click → emit event:selected for detail view.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { relativeTime } from '@/utils/time';
import type { NormalizedEvent } from '@/sources/base-source';

const FORECAST_CATEGORIES = ['Geopolitics', 'Conflicts', 'Elections', 'Other'] as const;
type ForecastCategory = typeof FORECAST_CATEGORIES[number];

function isForecastEvent(ev: NormalizedEvent): boolean {
  if (ev.source === 'prediction') return true;
  if (ev.tags?.some(t => t === 'forecast' || t === 'prediction')) return true;
  return false;
}

export function createPredictionPanel(): HTMLElement {
  const container = document.createElement('div');
  container.className = 'prediction-panel';
  container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

  const contentArea = document.createElement('div');
  contentArea.style.cssText = 'padding: 12px;';
  container.appendChild(contentArea);

  const cleanupFns: (() => void)[] = [];
  let renderPending = false;

  function scheduleRender(): void {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      render();
    });
  }

  function extractProbability(ev: NormalizedEvent): number | null {
    for (const tag of ev.tags ?? []) {
      const match = tag.match(/^prob[:\-=](\d+)/i);
      if (match) return parseInt(match[1], 10);
    }
    const text = ev.description ?? ev.title;
    const pctMatch = text.match(/(\d{1,3})%/);
    if (pctMatch) return parseInt(pctMatch[1], 10);
    return null;
  }

  function inferCategory(ev: NormalizedEvent): ForecastCategory {
    const tags = ev.tags ?? [];
    const text = (ev.title + ' ' + (ev.description ?? '')).toLowerCase();

    for (const tag of tags) {
      const t = tag.toLowerCase();
      if (t === 'geopolitics' || t === 'geopolitical') return 'Geopolitics';
      if (t === 'conflict' || t === 'war' || t === 'military') return 'Conflicts';
      if (t === 'election' || t === 'vote' || t === 'political') return 'Elections';
    }

    if (text.includes('election') || text.includes('vote') || text.includes('president') || text.includes('prime minister') || text.includes('referendum')) return 'Elections';
    if (text.includes('war') || text.includes('invasion') || text.includes('conflict') || text.includes('military') || text.includes('attack')) return 'Conflicts';
    if (text.includes('sanction') || text.includes('tariff') || text.includes('nato') || text.includes('treaty') || text.includes('diplomacy') || text.includes('geopolit')) return 'Geopolitics';
    return 'Other';
  }

  function getProbabilityColor(pct: number): string {
    // Neutral blue gradient — probability isn't inherently good/bad
    // (e.g. 85% chance of war is high-risk, not "green/good")
    if (pct < 25) return '#64748b';       // slate — low confidence
    if (pct < 50) return '#6993d6';       // light steel blue
    if (pct < 75) return '#3b82f6';       // blue — moderate
    return '#2563eb';                      // strong blue — high confidence
  }

  function render(): void {
    const events = appStore.getState().events;
    const forecastEvents = events
      .filter(isForecastEvent)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    contentArea.textContent = '';

    // Summary
    const countHeader = document.createElement('div');
    countHeader.style.cssText = `
      display: flex; align-items: center; gap: 8px; margin-bottom: 12px;
      padding: 8px 10px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    const countNum = document.createElement('span');
    countNum.style.cssText = `font-size: 20px; font-weight: 700; font-family: var(--font-mono); color: ${forecastEvents.length > 0 ? 'var(--accent)' : 'var(--text-muted)'};`;
    countNum.textContent = String(forecastEvents.length);
    countHeader.appendChild(countNum);

    const countLabel = document.createElement('span');
    countLabel.style.cssText = 'font-size: 11px; color: var(--text-secondary);';
    countLabel.textContent = `active forecast${forecastEvents.length !== 1 ? 's' : ''}`;
    countHeader.appendChild(countLabel);

    contentArea.appendChild(countHeader);

    if (forecastEvents.length === 0) {
      const msg = document.createElement('div');
      msg.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic; padding: 16px 0; text-align: center;';
      msg.textContent = 'No prediction data available';
      contentArea.appendChild(msg);
      return;
    }

    // Group by category
    const grouped = new Map<ForecastCategory, NormalizedEvent[]>();
    for (const cat of FORECAST_CATEGORIES) grouped.set(cat, []);
    for (const ev of forecastEvents) {
      const cat = inferCategory(ev);
      grouped.get(cat)!.push(ev);
    }

    let rendered = 0;
    for (const cat of FORECAST_CATEGORIES) {
      const catEvents = grouped.get(cat)!;
      if (catEvents.length === 0) continue;

      const sectionHeader = document.createElement('div');
      sectionHeader.style.cssText = `
        font-size: 11px; font-weight: 600; color: var(--text-secondary);
        margin: 10px 0 6px; text-transform: uppercase; letter-spacing: 0.05em;
      `;
      sectionHeader.textContent = `${cat} (${catEvents.length})`;
      contentArea.appendChild(sectionHeader);

      for (const ev of catEvents) {
        if (rendered >= 60) break;
        rendered++;

        const prob = extractProbability(ev);

        const row = document.createElement('div');
        row.style.cssText = `
          padding: 8px; margin-bottom: 4px; border-radius: 4px;
          background: var(--bg-secondary); cursor: pointer;
        `;

        row.onclick = () => eventBus.emit('event:selected', ev);
        row.title = 'Click for details';

        const titleEl = document.createElement('div');
        titleEl.style.cssText = 'font-size: 12px; font-weight: 500; color: var(--text-primary); margin-bottom: 6px;';
        titleEl.textContent = ev.title;
        row.appendChild(titleEl);

        if (prob !== null) {
          const barContainer = document.createElement('div');
          barContainer.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 4px;';

          const barBg = document.createElement('div');
          barBg.style.cssText = 'flex: 1; height: 8px; border-radius: 4px; background: var(--bg-primary);';

          const barFill = document.createElement('div');
          const color = getProbabilityColor(prob);
          barFill.style.cssText = `
            height: 100%; border-radius: 4px;
            width: ${Math.min(prob, 100)}%;
            background: ${color};
            transition: width 300ms ease;
          `;
          barBg.appendChild(barFill);
          barContainer.appendChild(barBg);

          const pctLabel = document.createElement('span');
          pctLabel.style.cssText = `font-size: 12px; font-weight: 700; font-family: var(--font-mono); color: ${color}; min-width: 36px; text-align: right;`;
          pctLabel.textContent = `${prob}%`;
          barContainer.appendChild(pctLabel);

          row.appendChild(barContainer);
        }

        const time = document.createElement('div');
        time.style.cssText = 'font-size: 10px; color: var(--text-muted);';
        time.textContent = relativeTime(ev.timestamp);
        row.appendChild(time);

        contentArea.appendChild(row);
      }
    }
  }

  render();

  cleanupFns.push(
    appStore.subscribe(
      (s) => s.events,
      () => scheduleRender(),
    ),
  );

  (container as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const fn of cleanupFns) fn();
    cleanupFns.length = 0;
  };

  return container;
}
